"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
function HeaderContent() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header data-section-id="16281" className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-500 border-b ${isScrolled ? "bg-[#002147]/80 backdrop-blur-lg border-white/10 py-3" : "bg-[#002147] border-white/5 py-4"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between">
          {}
          <div className="flex-shrink-0">
            <Link to="/HomePage">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1591494/img/logo.png" alt="BahamaVista Activities Center" className="h-9 w-auto object-contain brightness-0 invert" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex flex-1 justify-center px-8">
            {loading ? <div className="flex gap-4">
                {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-20 bg-white/10 animate-pulse rounded-sm" />)}
              </div> : <NavigationMenu>
                <NavigationMenuList className="gap-8">
                  {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                      <NavigationMenuLink asChild>
                        <Link to={item.href || "#"} className="text-sm font-medium tracking-wide text-white uppercase transition-all duration-300 hover:text-[#ff5733] hover:bg-transparent hover:underline hover:decoration-[#ff5733] hover:underline-offset-8 focus:bg-transparent">
                          {item.label}
                        </Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>)}
                </NavigationMenuList>
              </NavigationMenu>}
          </div>

          {}
          <div className="hidden md:flex items-center gap-4">
            <Link to="#login">
              <Button variant="ghost" className="text-white font-medium hover:text-[#007bff] hover:bg-white/5 transition-colors">
                Sign In
              </Button>
            </Link>
            <Link to="#contact">
              <Button className="bg-[#ff5733] text-white font-bold hover:bg-[#ff5733]/90 shadow-lg rounded-md px-6">
                Book Now
              </Button>
            </Link>

            <div className="w-px h-6 bg-white/10 mx-1" />

            <Select defaultValue="en">
              <SelectTrigger className="w-[70px] border-none shadow-none bg-transparent text-white font-mono text-xs tracking-widest focus:ring-0 px-0 gap-1">
                <SelectValue placeholder="Lang" />
              </SelectTrigger>
              <SelectContent align="end" className="bg-[#002147] border-white/10">
                <SelectItem value="en" className="text-white focus:bg-white/10 focus:text-white">EN</SelectItem>
                <SelectItem value="es" className="text-white focus:bg-white/10 focus:text-white">ES</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {}
          <div className="md:hidden flex items-center gap-4">
            <Select defaultValue="en">
              <SelectTrigger className="w-[60px] border-none shadow-none bg-transparent text-white focus:ring-0 px-0 gap-1">
                <SelectValue placeholder="Lang" />
              </SelectTrigger>
              <SelectContent align="end" className="bg-[#002147] border-white/10">
                <SelectItem value="en" className="text-white focus:bg-white/10">EN</SelectItem>
                <SelectItem value="es" className="text-white focus:bg-white/10">ES</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white hover:text-[#ff5733] hover:bg-white/5" aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
            </Button>
          </div>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-[#002147] border-b border-white/10 shadow-2xl py-8 px-6 flex flex-col gap-6 animate-in slide-in-from-top-5 fade-in duration-300">
          <nav className="flex flex-col gap-4">
            {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-xl font-bold tracking-tight text-white/90 py-3 border-b border-white/5 hover:text-[#ff5733] transition-colors" onClick={() => setIsMobileMenuOpen(false)}>
                {item.label}
              </Link>)}
          </nav>
          <div className="flex flex-col gap-4 mt-4">
            <Link to="#login" onClick={() => setIsMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-center text-white border border-white/20 hover:bg-white/5">
                Sign In
              </Button>
            </Link>
            <Link to="#contact" onClick={() => setIsMobileMenuOpen(false)}>
              <Button className="w-full bg-[#ff5733] text-white font-bold py-6">
                Book Now
              </Button>
            </Link>
          </div>
        </div>}
    </header>;
}
export default function MainHeader() {
  return <section id="header" className="dark bg-background text-foreground relative">
      <MenuProvider menu_id="16" className="w-full">
        <HeaderContent />
      </MenuProvider>
      {}
      <div className="h-[76px] md:h-[88px]" />
    </section>;
}